import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { OneWayInServicesService, OneWayInServiceUpsertDto, PublicationState } from 'src/api/v2';
import {
  EEA_ARTICLES_KEY,
  ONE_WAY_IN_SERVICE,
  ONE_WAY_IN_SERVICES,
  ONE_WAY_IN_SERVICES_BY_ID,
} from 'src/constants/hook-keys';
import { getServiceStatus, ServiceStatuses } from 'src/features/services/utils/service-utils';

const useOneWayInServices = (clusterId?: string) => {
  const [oneWayInServicesService] = useState(new OneWayInServicesService());

  const queryClient = useQueryClient();

  const oneWayInOpportunityServicesQuery = useQuery({
    queryKey: clusterId ? [ONE_WAY_IN_SERVICES_BY_ID, clusterId] : [ONE_WAY_IN_SERVICES],
    queryFn: () => oneWayInServicesService.getOneWayInServices({ clusterId }),
    enabled: true,
    retry: false,
    staleTime: 1000 * 60 * 30, // 30 min refetching on mount
    refetchOnMount: true,
    keepPreviousData: false,
  });

  const {
    clusterServices,
    allServices,
    allActiveServices,
    activeClusterServices,
    inactiveClusterServices,
    draftClusterServices,
  } = useMemo(() => {
    if (!oneWayInOpportunityServicesQuery.data) {
      return {
        clusterServices: [],
        allServices: [],
      };
    }
    const clusterServices = oneWayInOpportunityServicesQuery.data.filter(
      (x) => x.clusterId === clusterId && x.state !== PublicationState.Deleted,
    );
    const removeDeleted = oneWayInOpportunityServicesQuery.data.filter(
      (x) => x.state !== PublicationState.Deleted,
    );

    return {
      clusterServices: clusterServices,
      allServices: removeDeleted.filter((x) => x.state !== PublicationState.Deleted),
      allActiveServices: removeDeleted.filter((x) => getServiceStatus(x) === ServiceStatuses.Active),
      activeClusterServices: clusterServices.filter((x) => getServiceStatus(x) === ServiceStatuses.Active),
      inactiveClusterServices: clusterServices.filter(
        (x) => getServiceStatus(x) === ServiceStatuses.Inactive,
      ),
      draftClusterServices: clusterServices.filter((x) => getServiceStatus(x) === ServiceStatuses.Draft),
    };
  }, [oneWayInOpportunityServicesQuery.data, clusterId]);

  const upsertService = useMutation({
    mutationFn: (service: OneWayInServiceUpsertDto) => {
      return oneWayInServicesService.upsert(service);
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries([ONE_WAY_IN_SERVICES]);
      queryClient.invalidateQueries([ONE_WAY_IN_SERVICES_BY_ID]);
      queryClient.invalidateQueries([EEA_ARTICLES_KEY]);
      queryClient.invalidateQueries([ONE_WAY_IN_SERVICE, res.id]);
    },
  });

  const deleteService = useMutation({
    mutationFn: (serviceId: string) => {
      return oneWayInServicesService.delete(serviceId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries([ONE_WAY_IN_SERVICES]);
      queryClient.invalidateQueries([ONE_WAY_IN_SERVICES_BY_ID]);
      queryClient.resetQueries([ONE_WAY_IN_SERVICES]);
      queryClient.invalidateQueries([EEA_ARTICLES_KEY]);
    },
  });

  return {
    clusterServices,
    allServices,
    upsertService,
    deleteService,
    allActiveServices: allActiveServices || [],
    isLoading: oneWayInOpportunityServicesQuery.isLoading,
    activeClusterServices: activeClusterServices || [],
    inactiveClusterServices: inactiveClusterServices || [],
    draftClusterServices: draftClusterServices || [],
  };
};

export default useOneWayInServices;
